// PDF Export utility functions for Calculator results
// Separates data preparation from PDF rendering

import { formatCurrency, formatNumber } from './utils';
import { regionTranslations } from './regionTranslations';
import { fundingTranslations } from './calculatorTranslations';
import { EU_FUNDING_PROGRAMS } from './calculatorConstants';
import { REGION_INFO, REGIONAL_GROWTH, REGIONAL_PRICES } from './regionDefaults';

/**
 * Prepare calculator data for PDF export
 * @param {Object} params - Calculator parameters and results
 * @returns {Object} Structured data ready for PDF generation
 */
export const prepareExportData = ({
  name,
  region,
  species,
  area,
  period,
  treeData,
  discountRate,
  results,
  resultsWithFunding,
  enableEUFunding,
  selectedFunding,
  language = 'sv'
}) => {
  const rt = regionTranslations[language] || regionTranslations.sv;
  const ft = fundingTranslations[language] || fundingTranslations.sv;
  
  // Species names
  const speciesNames = {
    sv: { gran: 'Gran (Picea abies)', tall: 'Tall (Pinus sylvestris)', bjork: 'Björk (Betula spp.)' },
    de: { gran: 'Fichte', tall: 'Kiefer', bjork: 'Birke' },
    en: { gran: 'Spruce', tall: 'Pine', bjork: 'Birch' },
    se: { gran: 'Guossa', tall: 'Biehtse', bjork: 'Soahki' }
  };
  
  const exportDate = new Date().toLocaleDateString(language === 'sv' ? 'sv-SE' : language === 'de' ? 'de-DE' : 'en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  return {
    // Header
    title: name || (language === 'sv' ? 'Skogsberäkning' : language === 'de' ? 'Waldberechnung' : 'Forest Calculation'),
    subtitle: language === 'sv' ? 'Ekonomisk analys av skogsskötsel' : language === 'de' ? 'Wirtschaftliche Analyse der Waldbewirtschaftung' : 'Economic Analysis of Forest Management',
    exportDate,
    
    // Basic info
    region: rt.regions[region] || region,
    regionKey: region,
    species: speciesNames[language]?.[species] || species,
    speciesKey: species,
    area,
    period,
    
    // Input values
    inputs: {
      growthManaged: treeData.growth_managed,
      growthNeglected: treeData.growth_neglected,
      priceTimber: treeData.price_timber,
      pricePulpwood: treeData.price_pulpwood,
      clearingCost: treeData.clearing_cost,
      plantingCost: treeData.planting_cost,
      thinningCost: treeData.thinning_cost,
      discountRate
    },
    
    // Results
    results: {
      totalNetBenefit: results?.total_net_benefit || 0,
      breakevenYear: results?.breakeven_year,
      finalVolume: results?.results?.[results.results.length - 1]?.managed_volume || 0,
      totalRevenue: results?.results?.reduce((sum, r) => sum + (r.extra_revenue || 0), 0) || 0,
      totalCosts: results?.results?.reduce((sum, r) => sum + (r.management_cost || 0), 0) || 0
    },
    
    // EU Funding (if enabled)
    funding: enableEUFunding && selectedFunding !== 'none' ? {
      enabled: true,
      program: EU_FUNDING_PROGRAMS[selectedFunding]?.names[language] || selectedFunding,
      programKey: selectedFunding,
      subsidyRates: EU_FUNDING_PROGRAMS[selectedFunding],
      resultsWithFunding: {
        totalNetBenefit: resultsWithFunding?.total_net_benefit || 0,
        breakevenYear: resultsWithFunding?.breakeven_year,
        savings: (resultsWithFunding?.total_net_benefit || 0) - (results?.total_net_benefit || 0)
      }
    } : { enabled: false },
    
    // Regional data source info
    dataSource: {
      region: REGION_INFO[region] || {},
      growthSource: 'SLU Riksskogstaxeringen (2024/2025)',
      priceSource: 'Skogsstyrelsen Virkesprisstatistik (Q4 2025)',
      costSource: 'Skogforsk Kostnadsstatistik (2024)',
      methodology: language === 'sv' 
        ? 'Regionala standardvärden baseras på riksskogsstatistik och justeras efter lokala förhållanden. Värden representerar genomsnitt för regionen.'
        : language === 'de'
        ? 'Regionale Standardwerte basieren auf nationaler Forststatistik, angepasst an lokale Bedingungen. Werte repräsentieren Regionaldurchschnitte.'
        : 'Regional defaults based on national forest statistics, adjusted for local conditions. Values represent regional averages.'
    },
    
    // Yearly data (sampled for PDF)
    yearlyData: results?.results?.filter((_, i) => i % 5 === 0 || i === 0 || i === (results.results.length - 1)) || [],
    
    language
  };
};

/**
 * Get disclaimer text for PDF
 * @param {string} language - Current language
 * @returns {Object} Disclaimer texts
 */
export const getDisclaimerTexts = (language = 'sv') => {
  const disclaimers = {
    sv: {
      title: 'Metodisk anmärkning',
      main: 'Denna beräkning är en förenklad ekonomisk simulering och utgör inte ett officiellt fastighetsvärdering eller investeringsrådgivning. Faktiska resultat kan avvika beroende på lokala förhållanden, marknadsförändringar och oförutsedda händelser.',
      assumptions: 'Beräkningen förutsätter konstanta priser och tillväxttakter över hela perioden. I verkligheten varierar dessa faktorer.',
      liability: 'Skogskalkyl.se ansvarar inte för beslut baserade på dessa beräkningar. Konsultera en professionell skogsekonom för väsentliga investeringsbeslut.',
      dataNote: 'Regionala standardvärden är baserade på genomsnittlig statistik från officiella källor (SLU, Skogsstyrelsen, Skogforsk). Individuella fastigheter kan avvika betydligt från dessa medelvärden.'
    },
    de: {
      title: 'Methodischer Hinweis',
      main: 'Diese Berechnung ist eine vereinfachte wirtschaftliche Simulation und stellt keine offizielle Immobilienbewertung oder Anlageberatung dar. Tatsächliche Ergebnisse können je nach örtlichen Gegebenheiten, Marktveränderungen und unvorhergesehenen Ereignissen abweichen.',
      assumptions: 'Die Berechnung setzt konstante Preise und Wachstumsraten über den gesamten Zeitraum voraus. In der Realität variieren diese Faktoren.',
      liability: 'Skogskalkyl.se übernimmt keine Haftung für Entscheidungen, die auf diesen Berechnungen basieren. Konsultieren Sie einen professionellen Forstwirt für wesentliche Investitionsentscheidungen.',
      dataNote: 'Regionale Standardwerte basieren auf Durchschnittsstatistiken aus offiziellen Quellen (SLU, Skogsstyrelsen, Skogforsk). Einzelne Grundstücke können erheblich von diesen Mittelwerten abweichen.'
    },
    en: {
      title: 'Methodological Note',
      main: 'This calculation is a simplified economic simulation and does not constitute an official property valuation or investment advice. Actual results may vary depending on local conditions, market changes, and unforeseen events.',
      assumptions: 'The calculation assumes constant prices and growth rates throughout the period. In reality, these factors vary.',
      liability: 'Skogskalkyl.se is not responsible for decisions based on these calculations. Consult a professional forestry economist for significant investment decisions.',
      dataNote: 'Regional default values are based on average statistics from official sources (SLU, Skogsstyrelsen, Skogforsk). Individual properties may deviate significantly from these averages.'
    },
    se: {
      title: 'Metodalaš čilgehus',
      main: 'Dát rehkenastin lea álkes ekonomalaš simulašuvdna ii ge atte virggálaš opmodatárvvoštallama dahje investerenrávvagiid.',
      assumptions: 'Rehkenastin dáhpá ahte hattit ja šaddanruhta bisuhit oktilis áigodagas.',
      liability: 'Skogskalkyl.se ii vástit mearrádusain mat vuođđuduvvet dáidda rehkenastimiin.',
      dataNote: 'Guovllu standardárvvut vuođđuduvvet virggálaš gálduin statistihkkii (SLU, Skogsstyrelsen, Skogforsk).'
    }
  };
  
  return disclaimers[language] || disclaimers.sv;
};

/**
 * Get assumption texts based on input data
 * @param {Object} exportData - Prepared export data
 * @param {string} language - Current language
 * @returns {Array} List of assumption strings
 */
export const getAssumptionTexts = (exportData, language = 'sv') => {
  const { inputs, region, species, funding } = exportData;
  
  const assumptions = {
    sv: [
      `Tillväxt för skött skog: ${inputs.growthManaged} m³sk/ha/år (baserat på ${region} genomsnitt för ${species})`,
      `Tillväxt för försummad skog: ${inputs.growthNeglected} m³sk/ha/år`,
      `Timmerpris: ${formatNumber(inputs.priceTimber)} SEK/m³fub (aktuellt regionalt pris)`,
      `Massavedspris: ${formatNumber(inputs.pricePulpwood)} SEK/m³fub`,
      `Röjningskostnad: ${formatNumber(inputs.clearingCost)} SEK/ha`,
      `Planteringskostnad: ${formatNumber(inputs.plantingCost)} SEK/ha`,
      `Gallringskostnad: ${formatNumber(inputs.thinningCost)} SEK/m³`,
      `Diskonteringsränta: ${inputs.discountRate}% (real ränta efter inflation)`
    ],
    de: [
      `Zuwachs bewirtschafteter Wald: ${inputs.growthManaged} m³/ha/Jahr (basierend auf ${region} Durchschnitt)`,
      `Zuwachs vernachlässigter Wald: ${inputs.growthNeglected} m³/ha/Jahr`,
      `Stammholzpreis: ${formatNumber(inputs.priceTimber)} SEK/m³`,
      `Industrieholzpreis: ${formatNumber(inputs.pricePulpwood)} SEK/m³`,
      `Läuterungskosten: ${formatNumber(inputs.clearingCost)} SEK/ha`,
      `Pflanzungskosten: ${formatNumber(inputs.plantingCost)} SEK/ha`,
      `Durchforstungskosten: ${formatNumber(inputs.thinningCost)} SEK/m³`,
      `Diskontierungszins: ${inputs.discountRate}% (Realzins)`
    ],
    en: [
      `Managed forest growth: ${inputs.growthManaged} m³/ha/year (based on ${region} average)`,
      `Neglected forest growth: ${inputs.growthNeglected} m³/ha/year`,
      `Sawlog price: ${formatNumber(inputs.priceTimber)} SEK/m³`,
      `Pulpwood price: ${formatNumber(inputs.pricePulpwood)} SEK/m³`,
      `Clearing cost: ${formatNumber(inputs.clearingCost)} SEK/ha`,
      `Planting cost: ${formatNumber(inputs.plantingCost)} SEK/ha`,
      `Thinning cost: ${formatNumber(inputs.thinningCost)} SEK/m³`,
      `Discount rate: ${inputs.discountRate}% (real rate)`
    ]
  };
  
  let result = assumptions[language] || assumptions.sv;
  
  if (funding.enabled) {
    const fundingNote = language === 'sv' 
      ? `EU-bidrag aktiverat: ${funding.program}`
      : language === 'de'
      ? `EU-Förderung aktiviert: ${funding.program}`
      : `EU funding enabled: ${funding.program}`;
    result.push(fundingNote);
  }
  
  return result;
};

export default {
  prepareExportData,
  getDisclaimerTexts,
  getAssumptionTexts
};
